import React, { useState } from 'react';
import axios from 'axios';
import { User, Lock, LogIn, GraduationCap, UserCog, UserPlus } from 'lucide-react';
import { Link } from 'react-router-dom';

const Login = () => {
    const [credentials, setCredentials] = useState({ 
        username: '', 
        password: '',
        role: 'admin' 
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleLogin = async (e) => {
        e.preventDefault();
        setError(''); 
        setLoading(true);
        try {
            const res = await axios.post('/api/login', {
                username: credentials.username,
                password: credentials.password
            });
            
            if (res.status === 200) {
                // Store full user data (including user_id, user_type, etc.)
                localStorage.setItem('user', JSON.stringify(res.data));
                
                // Role-based dashboard routing using actual user_type from backend
                const userType = res.data.user_type?.toLowerCase();
                
                switch(userType) {
                    case 'admin':
                        window.location.href = '/admin-dashboard';
                        break;
                    case 'faculty':
                        window.location.href = '/faculty-dashboard';
                        break;
                    case 'student':
                        window.location.href = '/student-dashboard';
                        break;
                    default:
                        setError('Unknown user type received from server');
                }
            }
        } catch (err) {
            setError(err.response?.data?.message || 
                    err.response?.status === 401 ? 'Invalid credentials' : 'Connection failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={styles.rcmasLoginPage}>
            <div style={styles.bgPattern}></div>
            
            <div style={styles.loginContainer}>
                {/* RCMAS Branding */}
                <div style={styles.branding}>
                    <div style={styles.collegeLogo}>
                        <GraduationCap size={48} style={{ color: '#0f766e' }} />
                        <h1 style={styles.rcmasName}>RCMAS</h1>
                        <span style={styles.tagline}>RE-LEARNING PLATFORM</span>
                    </div>
                    <div style={styles.heroText}>
                        <h2 style={styles.heroTextH2}>RE-LEARNING PLATFORM</h2>
                        <p style={styles.heroTextP}>-</p>
                    </div>
                </div>

                {/* Login Form */}
                <div style={styles.loginForm}>
                    <div style={styles.formHeader}>
                        <h3 style={styles.formHeaderH3}>RCMAS RE-LEARNING PLATFORM</h3>
                        <p style={styles.formHeaderP}>Sign in with your credentials</p>
                    </div>

                    {error && <div style={styles.errorBox}>{error}</div>}
                    
                    <form onSubmit={handleLogin} style={styles.form}>
                        <div style={styles.inputGroup}>
                            <User size={20} style={styles.inputIcon} />
                            <input 
                                type="text" 
                                placeholder="College ID / Username" 
                                style={styles.input}
                                value={credentials.username}
                                onChange={(e) => setCredentials({...credentials, username: e.target.value})} 
                                required 
                            />
                        </div>

                        <div style={styles.inputGroup}>
                            <Lock size={20} style={styles.inputIcon} />
                            <input 
                                type="password" 
                                placeholder="Password" 
                                style={styles.input}
                                value={credentials.password}
                                onChange={(e) => setCredentials({...credentials, password: e.target.value})} 
                                required 
                            />
                        </div>

                        <div style={styles.inputGroup}>
                            <UserCog size={20} style={styles.inputIcon} />
                            <select 
                                style={styles.inputSelect}
                                value={credentials.role}
                                onChange={(e) => setCredentials({...credentials, role: e.target.value})}
                            >
                                <option value="admin">👑 Administrator</option>
                                <option value="faculty">👩‍🏫 Faculty</option>
                                <option value="student">👨‍🎓 Student</option>
                            </select>
                        </div>

                     
                        <button type="submit" disabled={loading} style={{
                            ...styles.loginBtn,
                            ...(loading && styles.loginBtnDisabled)
                        }}>
                            {loading ? (
                                <>
                                    <div style={styles.spinner}></div>
                                    Signing In...
                                </>
                            ) : (
                                <>
                                    <LogIn size={18} />
                                    Enter RCMAS RE-LEARNING PLATFORM
                                </>
                            )}
                        </button>
                    </form>

                    {/* Student Registration Link */}
                    <div style={{ marginTop: '25px', textAlign: 'center' }}>
                        <p style={{ margin: '0 0 12px', color: '#64748b', fontSize: '14px' }}>
                            New Student?
                        </p>
                        <Link to="/register" style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '12px 24px',
                            background: 'linear-gradient(135deg, #1e3a8a 0%, #0f766e 100%)',
                            color: '#fff',
                            borderRadius: '10px',
                            textDecoration: 'none',
                            fontWeight: '600',
                            fontSize: '14px',
                            transition: 'all 0.3s ease'
                        }}>
                            <UserPlus size={16} />
                            Register Here
                        </Link>
                    </div>

                    <div style={styles.footerText}>
                        <p style={styles.footerTextP}>© 2026 RCMAS RE-LEARNING PLATFORM. Rajagiri College of Management & Applied Sciences</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

const styles = {
    rcmasLoginPage: { 
        minHeight: '100vh', 
        background: 'linear-gradient(135deg, #1e3a8a 0%, #0f766e 50%, #15803d 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        position: 'relative',
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
        padding: '20px'
    },
    bgPattern: {
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
        backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.08\'%3E%3Ccircle cx=\'30\' cy=\'30\' r=\'2\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
        pointerEvents: 'none'
    },
    loginContainer: {
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px',
        maxWidth: '1000px', width: '90%',
        background: 'rgba(255,255,255,0.97)',
        backdropFilter: 'blur(20px)',
        borderRadius: '24px',
        boxShadow: '0 25px 60px rgba(30,58,138,0.3)',
        zIndex: 1,
        overflow: 'hidden'
    },
    branding: {
        padding: '60px 40px',
        background: 'linear-gradient(145deg, rgba(15,118,110,0.15), rgba(21,128,61,0.15))',
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
        color: '#1e293b',
        borderTopLeftRadius: '24px',
        borderBottomLeftRadius: '24px'
    },
    collegeLogo: {
        display: 'flex', alignItems: 'center', flexDirection: 'column', gap: '10px',
        marginBottom: '40px', textAlign: 'center'
    },
    rcmasName: {
        fontSize: '36px', fontWeight: '800', color: '#1e3a8a',
        letterSpacing: '-0.02em', margin: 0
    },
    tagline: {
        fontSize: '14px', color: '#0f766e', fontWeight: '500', 
        letterSpacing: '0.05em'
    },
    heroText: { textAlign: 'center' },
    heroTextH2: { 
        fontSize: '28px', fontWeight: '600', margin: '0 0 15px 0', color: '#1e293b' 
    },
    heroTextP: { 
        fontSize: '16px', lineHeight: '1.7', color: '#475569', margin: 0 
    },
    loginForm: { padding: '60px 50px', display: 'flex', flexDirection: 'column' },
    formHeader: { textAlign: 'center', marginBottom: '35px' },
    formHeaderH3: { 
        fontSize: '26px', fontWeight: '600', color: '#1e293b', margin: '0 0 8px 0' 
    },
    formHeaderP: { 
        color: '#64748b', fontSize: '15px', margin: 0 
    },
    form: { width: '100%' },
    inputGroup: { position: 'relative', marginBottom: '20px' },
    inputIcon: { 
        position: 'absolute', left: '16px', top: '50%', 
        transform: 'translateY(-50%)', color: '#a0aec0', zIndex: 1
    },
    input: { 
        width: '100%', padding: '16px 16px 16px 50px', 
        border: '2px solid #e2e8f0', borderRadius: '12px',
        background: '#f8fafc', transition: 'all 0.3s ease',
        fontSize: '16px', boxSizing: 'border-box'
    },
    inputSelect: {
        width: '100%', padding: '16px 50px 16px 16px', 
        border: '2px solid #e2e8f0', borderRadius: '12px',
        background: '#f8fafc url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 20 20\'%3E%3Cpath stroke=\'%23a0aec0\' stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'1.5\' d=\'m6 8 4 4 4-4\'/%3E%3C/svg%3E") no-repeat right 16px center',
        backgroundSize: '16px', transition: 'all 0.3s ease',
        fontSize: '16px', color: '#1e293b', cursor: 'pointer',
        boxSizing: 'border-box'
    },
    formFooter: {
        display: 'flex', justifyContent: 'space-between', 
        alignItems: 'center', marginBottom: '25px', fontSize: '14px'
    },
    checkboxLabel: {
        display: 'flex', alignItems: 'center', gap: '8px',
        color: '#4a5568', cursor: 'pointer'
    },
    checkbox: { width: '16px', height: '16px', accentColor: '#1e3a8a' },
    forgotBtn: {
        background: 'none', border: 'none', color: '#1e3a8a',
        fontSize: '14px', fontWeight: '500', cursor: 'pointer',
        textDecoration: 'underline', padding: 0
    },
    loginBtn: { 
        width: '100%', padding: '16px', 
        background: 'linear-gradient(135deg, #1e3a8a 0%, #0f766e 100%)', 
        color: '#fff', border: 'none', borderRadius: '12px',
        fontSize: '16px', fontWeight: '600',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
        cursor: 'pointer', transition: 'all 0.3s ease'
    },
    loginBtnDisabled: {
        opacity: 0.7, cursor: 'not-allowed', background: '#64748b'
    },
    spinner: {
        width: 16, height: 16, border: '2px solid transparent',
        borderTop: '2px solid #fff', borderRadius: '50%',
        animation: 'spin 1s linear infinite'
    },
    errorBox: { 
        background: 'linear-gradient(90deg, #fee2e2, #fecaca)', 
        color: '#dc2626', padding: '14px', borderRadius: '10px',
        borderLeft: '4px solid #ef4444', marginBottom: '20px',
        fontSize: '14px'
    },
    testCredentials: {
        marginTop: '25px', padding: '20px',
        background: 'rgba(59, 130, 246, 0.05)', borderRadius: '12px',
        border: '1px solid rgba(59, 130, 246, 0.2)'
    },
    testTitle: {
        margin: '0 0 12px 0', color: '#1e40af', fontSize: '14px',
        fontWeight: '600'
    },
    credList: {
        fontSize: '13px', lineHeight: '1.6', color: '#1e293b'
    },
    footerText: {
        textAlign: 'center', marginTop: '30px', paddingTop: '20px',
        borderTop: '1px solid #e2e8f0'
    },
    footerTextP: { 
        margin: 0, fontSize: '13px', color: '#64748b' 
    }
};

// Add CSS animation to document head
const style = document.createElement('style');
style.textContent = `
    @keyframes spin { to { transform: rotate(360deg); } }
`;
document.head.appendChild(style);

export default Login;